import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import type { TrialStatus } from '../services/api-client';

interface TrialExpiredPopupProps {
  status: TrialStatus | null;
}

export default function TrialExpiredPopup({ status }: TrialExpiredPopupProps) {
  const { user } = useUser();
  const dismissKey = `trial_expired_popup_dismissed:${user?.id ?? 'anon'}`;
  const [dismissed, setDismissed] = useState(
    () => sessionStorage.getItem(dismissKey) === 'true'
  );
  const dialogRef = useRef<HTMLDivElement>(null);

  // IS-INV-034: read active from API — never compute from dates client-side
  const shouldShow = status !== null && status.active === false && status.trialEnd !== null;

  useEffect(() => {
    if (!shouldShow || dismissed) return;
    dialogRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shouldShow, dismissed]);

  if (!shouldShow || dismissed) return null;

  const handleDismiss = () => {
    sessionStorage.setItem(dismissKey, 'true');
    setDismissed(true);
  };

  const handleDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm"
      onClick={handleDismiss}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="trial-expired-title"
        tabIndex={-1}
        className="bg-surface border border-border rounded-xl p-8 max-w-sm mx-4 text-center shadow-xl outline-none"
        onClick={e => e.stopPropagation()}
        onKeyDown={handleDialogKeyDown}
      >
        <h2 id="trial-expired-title" className="font-display text-2xl text-foreground mb-3 uppercase">
          Your free trial has ended
        </h2>
        <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
          Subscribe to restore premium access and keep your streak going.
        </p>
        <div className="flex flex-col gap-3">
          <Link
            to="/subscription"
            className="w-full px-6 py-3 bg-sport-accent text-white rounded-md font-emphasis text-sm
                       font-medium uppercase tracking-wide hover:opacity-90 transition-opacity duration-200 text-center"
            onClick={handleDismiss}
          >
            View Plans
          </Link>
          <button
            onClick={handleDismiss}
            className="w-full px-6 py-3 border border-border rounded-md font-emphasis text-sm
                       font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground
                       hover:border-foreground transition-colors duration-200"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}
